import { useState, useCallback, useRef } from 'react'

interface FileAttachment {
  id: string
  fileName: string
  fileUrl: string | null
}

interface UseCommentProps {
  onSubmit: (content: string, fileIds: string[], mentionedUserIds: string[]) => void
  uploadFileAction: (options: { files: FileList; isMultiple: boolean; saveFile: boolean }) => void
  uploadedFileId: string
  uploadFileLoading: boolean
  uploadedFileName: string
  uploadedFileUrl: string | null
  initialComment?: string
  initialAttachments?: FileAttachment[]
}

export const useComment = ({
  onSubmit,
  uploadFileAction,
  uploadedFileId,
  uploadedFileName,
  uploadedFileUrl,
  uploadFileLoading,
  initialComment,
  initialAttachments,
}: UseCommentProps) => {
  const [comment, setComment] = useState(initialComment ?? '')
  const [attachments, setAttachments] = useState<FileAttachment[]>(initialAttachments ?? [])
  const uploadSessionRef = useRef<{ sessionId: string; fileNames: Set<string> } | null>(null)

  // Handle file upload - accepts array of files and uploads them all at once
  const handleFileUpload = (files: File[], isMultiple: boolean) => {
    if (!Array.isArray(files) || files.length === 0) return

    // Create a new upload session to track which files belong to this upload
    const sessionId = `upload-${Date.now()}-${Math.random()}`
    const fileNames = new Set(files.map((f) => f.name))
    uploadSessionRef.current = { sessionId, fileNames }

    // Create FileList from File array
    const dataTransfer = new DataTransfer()
    files.forEach((file) => dataTransfer.items.add(file))
    const fileList = dataTransfer.files

    uploadFileAction({
      files: fileList,
      isMultiple: isMultiple,
      saveFile: true,
    })
  }

  // Handle successful file upload - handles both single file and array from Redux
  const handleFileUploadSuccess = useCallback(() => {
    if (!uploadFileLoading && uploadedFileId && uploadSessionRef.current) {
      // Handle both single object and array from Redux
      let files: any[] = []

      if (Array.isArray(uploadedFileId)) {
        // Filter out any null/undefined values and ensure we have valid file objects
        files = uploadedFileId.filter((f) => f != null)
      } else if (uploadedFileId && typeof uploadedFileId === 'object' && uploadedFileId !== null) {
        files = [uploadedFileId]
      } else if (uploadedFileId) {
        // It's a string ID or other primitive
        files = [{ id: uploadedFileId }]
      }

      // Only process if we have valid files
      if (files.length === 0) {
        return
      }

      // Only process files that match the current upload session
      const { fileNames } = uploadSessionRef.current
      const matchingFiles = files.filter((file: any) => {
        const fileName =
          typeof file === 'string' ? uploadedFileName : file?.name || uploadedFileName
        return fileNames.has(fileName)
      })

      if (matchingFiles.length === 0) {
        // Files don't match current session, ignore them
        return
      }

      const newAttachments: FileAttachment[] = []

      matchingFiles.forEach((file: any) => {
        if (!file) return

        const fileId = typeof file === 'string' ? file : file?.id
        const fileName =
          typeof file === 'string' ? uploadedFileName : file?.name || uploadedFileName
        const fileUrl = typeof file === 'string' ? uploadedFileUrl : file?.url || uploadedFileUrl

        if (fileId) {
          const newAttachment: FileAttachment = {
            id: fileId,
            fileName: fileName || 'Untitled',
            fileUrl: fileUrl || null,
          }
          newAttachments.push(newAttachment)
        }
      })

      // Add all new attachments that don't already exist
      if (newAttachments.length > 0) {
        setAttachments((prev) => {
          const existingIds = new Set(prev.map((att) => att.id))
          const toAdd = newAttachments.filter((att) => !existingIds.has(att.id))
          return toAdd.length > 0 ? [...prev, ...toAdd] : prev
        })
      }

      // Clear upload session and pending uploads after processing
      uploadSessionRef.current = null
    }
  }, [uploadFileLoading, uploadedFileId, uploadedFileName, uploadedFileUrl])

  // Handle comment submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!comment.trim() && attachments.length === 0) return

    const commentContent = {
      text: comment.trim(),
      attachments: attachments.map((att) => ({
        id: att.id,
        fileName: att.fileName,
        fileUrl: att.fileUrl,
      })),
    }

    onSubmit(
      JSON.stringify(commentContent),
      attachments.map((att) => att.id),
      []
    )
    setComment('')
    setAttachments([])
    // Clear upload session after submission
    uploadSessionRef.current = null
  }

  const removeAttachment = (id: string) => {
    setAttachments((prev) => prev.filter((att) => att.id !== id))
  }

  return {
    comment,
    setComment,
    attachments,
    setAttachments,
    handleFileUpload,
    handleFileUploadSuccess,
    handleSubmit,
    removeAttachment,
  }
}
